import { useEffect, useState } from 'react'

export const DOCK_MIN_WIDTH = 320
export const DOCK_MAX_WIDTH = 560
export const DOCK_STEP = 24

const DOCK_DEFAULT_WIDTH = 396
const STORAGE_KEY = 'reverAiDockWidth'

// Navbar plus the narrowest page column still worth reading. The panel may never
// eat into it, which is also why docking needs a viewport of at least that plus
// DOCK_MIN_WIDTH — narrower, and the panel overlays the page instead.
const RESERVED_FOR_PAGE = 880

export const DOCK_MEDIA_QUERY = '(min-width: 1280px)'

// Widest the panel may get on this viewport: a width dragged out on a large
// monitor must not crush the page after the user moves to a laptop.
const viewportMax = () =>
  Math.max(
    DOCK_MIN_WIDTH,
    Math.min(DOCK_MAX_WIDTH, (window.innerWidth || 0) - RESERVED_FOR_PAGE)
  )

// Storage throws in Safari private mode and is absent under the test runner; a
// forgotten width must not take the panel down with it.
const readStoredWidth = () => {
  try {
    const stored = Number(localStorage.getItem(STORAGE_KEY))
    return stored > 0
      ? Math.min(DOCK_MAX_WIDTH, Math.max(DOCK_MIN_WIDTH, Math.round(stored)))
      : DOCK_DEFAULT_WIDTH
  } catch {
    return DOCK_DEFAULT_WIDTH
  }
}

const store = (px: number) => {
  try {
    localStorage.setItem(STORAGE_KEY, String(px))
  } catch {
    /* width stays for this session only */
  }
}

export const useDockWidth = () => {
  const [preferred, setPreferred] = useState(readStoredWidth)
  const [maxWidth, setMaxWidth] = useState(viewportMax)
  const [isResizing, setIsResizing] = useState(false)

  useEffect(() => {
    const onResize = () => setMaxWidth(viewportMax())
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const width = Math.min(preferred, maxWidth)
  const clamp = (px: number) =>
    Math.min(maxWidth, Math.max(DOCK_MIN_WIDTH, Math.round(px)))

  const startResize = (event: React.PointerEvent) => {
    // Primary button only: pointerdown also fires for right-click, which would
    // start a drag while the context menu opens and swallow the pointerup.
    if (event.button !== 0) return
    // Keep the drag from selecting the page text it passes over.
    event.preventDefault()
    // Capture so a release outside the window (dragging off the right edge) and
    // touch/pen gestures still deliver the events that end the drag.
    event.currentTarget.setPointerCapture?.(event.pointerId)
    const startX = event.clientX
    const startWidth = width
    let latest = startWidth
    setIsResizing(true)

    const onMove = (e: PointerEvent) => {
      latest = clamp(startWidth + (startX - e.clientX))
      setPreferred(latest)
    }
    const onUp = () => {
      setIsResizing(false)
      store(latest)
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    // Touch/pen cancels the gesture instead of firing pointerup; without this
    // the move listener would keep resizing on every bare pointer move.
    window.addEventListener('pointercancel', onUp)
  }

  const nudgeWidth = (delta: number) => {
    const next = clamp(width + delta)
    setPreferred(next)
    store(next)
  }

  return { width, maxWidth, isResizing, startResize, nudgeWidth }
}
